import {
	type ArchiveFilter,
	type ArchiveItem,
	cleanStackName,
	defaultStackName,
	LEVELS,
	matchFilter,
	relaxations,
	type SizeKey,
	STACK_NAME_MAX,
	STATUS_KEYS,
	type StatusKey,
	withoutKey,
} from '@arrow/game-engine';
import { type FormEvent, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Checkbox, Sheet, VisuallyHidden } from '../../ui/index.ts';
import { LEVEL_NAME } from '../today-model.ts';
import { plural, relaxLabel } from './model.ts';

/** What the builder opens with: a new stack (maybe from a search's words), or one of yours to edit. */
export interface BuilderDraft {
	mode: 'new' | 'edit';
	/** The stack being edited. */
	id?: string;
	/** Its name (edit), or null to start from the rules' own. */
	name: string | null;
	filter: ArchiveFilter;
}

const STATUS_LABEL: Record<StatusKey, string> = { new: 'Not started', started: 'In progress', done: 'Done', perfect: 'Perfect', missed: 'Missed' };
const SIZE_LABEL: Record<SizeKey, string> = { any: 'Any size', small: 'Up to 8×8', medium: '9×9 and 10×10', big: '11×11 and up' };
const RANGE_LABEL = { all: 'Any time', '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days' } as const;
const DAY_LABEL = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];
const MONTH_LABEL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const count = (items: readonly ArchiveItem[], f: ArchiveFilter) => items.reduce((n, it) => n + (matchFilter(it, f) ? 1 : 0), 0);

/**
 * "New collection" / "Edit collection" (docs/mockups/play-2.html, F's builder): every rule as a real
 * control with how many puzzles each choice would give, the live total, the nearest
 * loosenings when nothing matches, and a name that follows the rules until you type one.
 * Save keeps it. Editing adds Delete, confirmed in place.
 */
export function StackBuilder({
	draft,
	items,
	newPlayer,
	onClose,
	onSave,
	onDelete,
}: {
	draft: BuilderDraft;
	/** The past dailies, with your state: what every count is of. */
	items: readonly ArchiveItem[];
	/** A new player can't have missed anything: no Missed. */
	newPlayer: boolean;
	onClose: () => void;
	onSave: (name: string, filter: ArchiveFilter) => void;
	onDelete: () => void;
}) {
	const [filter, setFilter] = useState<ArchiveFilter>(draft.filter);
	// The rules' own name is the placeholder (it follows them); an empty field saves under it.
	const [typed, setTyped] = useState(draft.name !== null && draft.name !== defaultStackName(draft.filter) ? draft.name : '');
	const [confirming, setConfirming] = useState(false);
	const keepRef = useRef<HTMLButtonElement>(null);
	const ids = useId();
	const auto = defaultStackName(filter);
	const total = useMemo(() => count(items, filter), [items, filter]);
	const relax = useMemo(() => (total === 0 ? relaxations(items, filter) : []), [items, filter, total]);
	const months = useMemo(() => {
		const seen = new Set<string>();
		for (const it of items) if (it.date) seen.add(it.date.slice(0, 7));
		return [...seen].sort().reverse();
	}, [items]);

	const set = (patch: Partial<ArchiveFilter>) => setFilter((f) => ({ ...f, ...patch }));
	const toggle = <T extends string>(list: readonly T[], v: T, order: readonly T[]) =>
		list.includes(v) ? list.filter((x) => x !== v) : order.filter((x) => x === v || list.includes(x));
	const n = (patch: Partial<ArchiveFilter>) => count(items, { ...filter, ...patch });
	// No Perfect chip (it's a kind of Done; search still knows "perfect"), unless this stack
	// already uses it. A new player can't have missed anything: no Missed.
	const statuses = STATUS_KEYS.filter((k) => filter.status.includes(k) || (k !== 'perfect' && (!newPlayer || k !== 'missed')));
	const when = filter.month ? `m:${filter.month.year}-${filter.month.month}` : filter.range;

	const save = (e: FormEvent) => {
		e.preventDefault();
		onSave(cleanStackName(typed, filter), filter);
	};

	const askDelete = () => setConfirming(true);
	// The question takes focus on its safe answer.
	useEffect(() => {
		if (confirming) keepRef.current?.focus();
	}, [confirming]);

	return (
		<Sheet open title={draft.mode === 'new' ? 'New collection' : 'Edit collection'} onClose={onClose} className="bld-sheet" testId="stack-builder">
			<form className="bld" onSubmit={save} noValidate>
				<label className="fld bld-name">
					<span>Name</span>
					<input
						className="field"
						type="text"
						autoComplete="off"
						enterKeyHint="done"
						maxLength={STACK_NAME_MAX}
						placeholder={auto}
						value={typed}
						onChange={(e) => setTyped(e.target.value)}
					/>
				</label>

				<fieldset className="grp">
					<legend className="leg">Level</legend>
					<div className="chipwrap">
						{LEVELS.map((l) => {
							const c = n({ levels: [l] });
							return (
								<Checkbox
									key={l}
									className="chip toggle ck"
									checked={filter.levels.includes(l)}
									onCheckedChange={() => set({ levels: toggle(filter.levels, l, LEVELS) })}
								>
									{LEVEL_NAME[l]}
									<small aria-hidden="true">{c}</small>
									<VisuallyHidden>
										, {c} {plural(c, 'puzzle')}
									</VisuallyHidden>
								</Checkbox>
							);
						})}
					</div>
				</fieldset>

				<fieldset className="grp">
					<legend className="leg">Status</legend>
					<div className="chipwrap">
						{statuses.map((k) => {
							const c = n({ status: [k] });
							const on = filter.status.includes(k);
							return (
								<Checkbox
									key={k}
									className="chip toggle ck"
									checked={on}
									disabled={c === 0 && !on}
									onCheckedChange={() => set({ status: toggle(filter.status, k, STATUS_KEYS) })}
								>
									{STATUS_LABEL[k]}
									<small aria-hidden="true">{c}</small>
									<VisuallyHidden>
										, {c} {plural(c, 'puzzle')}
									</VisuallyHidden>
								</Checkbox>
							);
						})}
					</div>
				</fieldset>

				<div className="grp2">
					<label className="fld">
						<span>Size</span>
						<span className="selw">
							<select className="field" value={filter.size} onChange={(e) => set({ size: e.target.value as SizeKey })}>
								{(['any', 'small', 'medium', 'big'] as const).map((k) => (
									<option key={k} value={k}>
										{SIZE_LABEL[k]} ({n({ size: k })})
									</option>
								))}
							</select>
						</span>
					</label>
					<label className="fld">
						<span>When</span>
						<span className="selw">
							<select
								className="field"
								value={when}
								onChange={(e) => {
									const v = e.target.value;
									if (v.startsWith('m:')) {
										const [y, m] = v.slice(2).split('-').map(Number);
										set({ month: { year: y!, month: m! }, range: 'all' });
									} else set({ range: v as ArchiveFilter['range'], month: null });
								}}
							>
								{(['all', '7', '30', '90'] as const).map((k) => (
									<option key={k} value={k}>
										{RANGE_LABEL[k]} ({n({ range: k, month: null })})
									</option>
								))}
								{months.map((ym) => {
									const [y, m] = ym.split('-').map(Number) as [number, number];
									return (
										<option key={ym} value={`m:${y}-${m}`}>
											{MONTH_LABEL[m - 1]} {y} ({n({ month: { year: y, month: m }, range: 'all' })})
										</option>
									);
								})}
							</select>
						</span>
					</label>
					<label className="fld">
						<span>Day</span>
						<span className="selw">
							<select className="field" value={filter.weekday ?? ''} onChange={(e) => set({ weekday: e.target.value === '' ? null : Number(e.target.value) })}>
								<option value="">Any day ({n({ weekday: null })})</option>
								{DAY_LABEL.map((d, i) => (
									<option key={d} value={i}>
										{d} ({n({ weekday: i })})
									</option>
								))}
							</select>
						</span>
					</label>
				</div>

				<div className="bld-foot">
					{confirming ? (
						<div className="bld-confirm" role="group" aria-labelledby={`${ids}-del`}>
							<p id={`${ids}-del`}>Delete “{cleanStackName(typed, filter)}”?</p>
							<span className="bld-acts">
								<button ref={keepRef} type="button" className="tlink" onClick={() => setConfirming(false)}>
									Keep it
								</button>
								<button type="button" className="btn md danger fit" onClick={onDelete}>
									Delete
								</button>
							</span>
						</div>
					) : (
						<>
							<p className="bld-total" role="status">
								{total === 0 ? 'No puzzles' : `${total} ${plural(total, 'puzzle')}`}
							</p>
							<span className="bld-acts">
								{draft.mode === 'edit' && (
									<button type="button" className="tlink" onClick={askDelete}>
										Delete
									</button>
								)}
								<button type="submit" className="btn md primary fit">
									Save
								</button>
							</span>
							{relax.length > 0 && (
								<div className="relax bld-relax">
									{relax.map((r) => (
										<button
											key={r.key}
											type="button"
											className="btn sm secondary"
											onClick={() => setFilter((f) => withoutKey(f, r.key))}
											aria-label={`${relaxLabel(r.key)}: ${r.adds} more ${plural(r.adds, 'puzzle')}`}
										>
											{relaxLabel(r.key)}
											<small>+{r.adds}</small>
										</button>
									))}
								</div>
							)}
						</>
					)}
				</div>
			</form>
		</Sheet>
	);
}
